import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import MonetizationPie from './MonetizationPie';
import MonetizationLegend from './MonetizationLegend';
import MonetizationBars from './MonetizationBars';
import Skeletons from '../Skeletons';

import {
  filtersSelector,
  monetizationSelector,
  monetizationSettingsSelector,
} from '../../selectors';
import { initParams } from '../../configs';
import { isUnified } from '../../../../utils/helpers';
import { STORE_LABELS, STORE_ICONS } from '../../configs/constants';

const { store, permissions } = initParams;
const isUnifiedStore = isUnified(store);

const PANEL_ORDER = isUnifiedStore ? ['both', 'itunes_connect', 'google_play'] : [store];

const MonetizationPanel = ({ storeKey, data, period }) => {
  const label = STORE_LABELS[storeKey] || storeKey;
  const iconClass = STORE_ICONS[storeKey] || '';

  const renderContent = () => {
    if (data.no_revenue) {
      return (
        <div className="d-flex flex-grow align-items-center justify-content-center">
          <p className="text-xs text-quiet text-center pb-xl">
            This publisher has no revenue in the {period} days
          </p>
        </div>
      );
    }
    if (data.empty) {
      return (
        <div className="d-flex align-items-center justify-content-center flex-grow text-bold text-sm publisher_page_monetization__empty_state">
          No data for {label}
        </div>
      );
    }
    if (isUnifiedStore) {
      return (
        <>
          <MonetizationPie series={data.series} />
          <MonetizationLegend data={data} />
        </>
      );
    }
    return <MonetizationBars data={data} />;
  };

  return (
    <div className="d-flex flex-column w-100 mb-xxs">
      {isUnifiedStore && (
        <p className="text-center">
          <i className={`${iconClass} mr-xxxs`} />
          {label}
        </p>
      )}
      {renderContent()}
    </div>
  );
};

MonetizationPanel.propTypes = {
  storeKey: T.string.isRequired,
  data: T.shape({
    series: T.arrayOf(T.object),
    iap_revenue: T.number,
    download_revenue: T.number,
    iap_percentage: T.number,
    downloads_percentage: T.number,
    no_revenue: T.bool,
    empty: T.bool,
  }).isRequired,
  period: T.number.isRequired,
};

const Monetization = () => {
  const filters = useSelector(filtersSelector);
  const monetization = useSelector(monetizationSelector);
  const { loaded, pending } = useSelector(monetizationSettingsSelector);

  return (
    <section
      className={`publisher_page_monetization ${
        isUnifiedStore && !permissions.isFlexUser ? 'b-t pt-xxl' : 'p-a-md h-100'
      }`}
    >
      <div className="mb-lg">
        <h2 className="text-md mb-xxs">Style of Monetization</h2>
        <p className="text-quiet m-b text-xxxs">(Last {filters.period} days)</p>
      </div>
      {pending || !loaded || !monetization ? (
        <div
          className={cx('d-grid grid-justify-items-center publisher_page_monetization__grid', {
            'publisher_page_monetization__grid--unified': isUnifiedStore,
          })}
        >
          {PANEL_ORDER.map((key) => (
            <React.Fragment key={key}>{Skeletons.monetizationPanel}</React.Fragment>
          ))}
        </div>
      ) : (
        <div>
          {!permissions.allowPaidData || permissions.isFlexUser ? (
            <div
              className="d-flex align-items-center justify-content-center pos-rel text-center p-a-xs"
              style={{ minHeight: '260px' }}
            >
              <p className="text-center text-xxxs center-y w-100 p-a-xs">
                {!permissions.prospect && !permissions.isFlexUser && (
                  <>
                    <a href="/pricing">Unlock access</a>
                    &nbsp;on a paid plan&nbsp;
                  </>
                )}
                {permissions.isFlexUser && <>Not included in your plan</>}
              </p>
              <div
                className="publisher_page_monetization__blur"
                role="img"
                aria-label="Style of monetization"
              />
            </div>
          ) : (
            <div
              className={cx('d-grid grid-justify-items-center publisher_page_monetization__grid', {
                'publisher_page_monetization__grid--unified': isUnifiedStore,
              })}
            >
              {PANEL_ORDER.map((key) => {
                const data = monetization[key];
                if (!data) return null;
                return (
                  <MonetizationPanel key={key} storeKey={key} data={data} period={filters.period} />
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default Monetization;
